/** CSS contract: css-library/components/03-layouts/pdf-viewer.css. */
import type { Ref } from 'react';
import { Pill } from './Pill';
import { PillCluster } from './PillCluster';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, HTMLAttributes, ReactNode, RefObject } from 'react';
import { cx } from '../lib/cx';
import { Button } from './Button';
import { Icon } from './Icon';
import { ScrollBar } from './ScrollBar';

const PDF_ZOOM = { min: 0.5, max: 3, step: 0.25 } as const;

type PdfZoomDirection = -1 | 0 | 1;

function stepPdfZoom(zoom: number, direction: PdfZoomDirection): number {
  if (direction === 0) return 1;
  return Math.min(PDF_ZOOM.max, Math.max(PDF_ZOOM.min, zoom + direction * PDF_ZOOM.step));
}

let defaultWorkerSrc: string | null = null;

export function configurePdfWorker(src: string): void {
  defaultWorkerSrc = src;
}

async function loadPdfJs() {
  // The viewer reads a global installed while the API module evaluates.
  const pdfjsLib = await import('pdfjs-dist');
  const viewerLib = await import('pdfjs-dist/web/pdf_viewer.mjs');
  return { pdfjsLib, viewerLib };
}

function pageMaxWidth(scrollerEl: HTMLElement): number | null {
  const value = getComputedStyle(scrollerEl).getPropertyValue('--pdf-page-max-width').trim();
  if (!value) return null;
  const match = /^(\d+(?:\.\d+)?)px$/.exec(value);
  const px = match ? Number(match[1]) : 0;
  if (px > 0) return px;
  console.error(`[PdfRegion] --pdf-page-max-width must be a px length, got "${value}"`);
  return null;
}

function usePdfViewer(
  scrollerRef: RefObject<HTMLDivElement | null>,
  pagesRef: RefObject<HTMLDivElement | null>,
  blob: Blob | null,
  scaleFactor: number,
  onError: (error: unknown) => void,
  zoom: number,
): void {
  const setZoomRef = useRef<((zoom: number) => void) | null>(null);
  const latest = useRef({ zoom, onError });
  useEffect(() => {
    latest.current = { zoom, onError };
    setZoomRef.current?.(zoom);
  });
  useEffect(() => {
    const scroller = scrollerRef.current;
    const pages = pagesRef.current;
    if (!blob || !scroller || !pages) return;
    const attempt = new AbortController();
    const { signal } = attempt;
    let dispose: (() => void) | undefined;
    void (async () => {
      const { pdfjsLib, viewerLib } = await loadPdfJs();
      if (signal.aborted) return;
      if (defaultWorkerSrc) pdfjsLib.GlobalWorkerOptions.workerSrc = defaultWorkerSrc;
      const eventBus = new viewerLib.EventBus();
      const linkService = new viewerLib.PDFLinkService({ eventBus });
      type ViewerOptions = ConstructorParameters<typeof viewerLib.PDFViewer>[0] & {
        abortSignal?: AbortSignal;
      };
      const options: ViewerOptions = {
        container: scroller,
        viewer: pages,
        eventBus,
        linkService,
        textLayerMode: 1,
        annotationMode: 0,
        abortSignal: signal,
      };
      const viewer = new viewerLib.PDFViewer(options);
      linkService.setViewer(viewer);
      let currentDoc: Awaited<ReturnType<typeof pdfjsLib.getDocument>['promise']> | null = null;
      dispose = () => {
        // setDocument(null) releases PDF.js window/document listeners.
        const detachable: { setDocument(doc: typeof currentDoc): void } = viewer;
        detachable.setDocument(null);
        if (currentDoc) void currentDoc.destroy().catch(() => {});
        viewer.cleanup();
      };
      let currentZoom = latest.current.zoom;
      let fitScale: number | null = null;
      const applyFitScale = () => {
        viewer.currentScaleValue = 'page-width';
        let scale = viewer.currentScale * scaleFactor;
        const maxWidth = pageMaxWidth(scroller);
        const page = maxWidth === null ? undefined : viewer.getPageView(viewer.currentPageNumber - 1);
        if (maxWidth !== null && page) {
          const widthAtScaleOne = page.viewport.width / viewer.currentScale;
          scale = Math.min(scale, maxWidth / widthAtScaleOne);
        }
        if (scale !== viewer.currentScale) viewer.currentScale = scale;
        fitScale = scale;
      };
      const applyZoom = () => {
        if (currentZoom === 1) applyFitScale();
        else if (fitScale !== null) viewer.currentScale = fitScale * currentZoom;
      };
      eventBus.on('pagesinit', () => {
        applyFitScale();
        if (currentZoom !== 1) applyZoom();
        scroller.scrollTop = 0;
      });
      setZoomRef.current = (next) => {
        if (next === currentZoom) return;
        currentZoom = next;
        if (fitScale === null) return;
        const before = viewer.currentScale;
        const across = middleAcross(scroller);
        applyZoom();
        scroller.scrollTop += (scroller.clientHeight / 2) * (viewer.currentScale / before - 1);
        keepMiddleAcross(scroller, across);
      };
      const data = await blob.arrayBuffer();
      if (signal.aborted) return;
      const doc = await pdfjsLib.getDocument({ data }).promise;
      if (signal.aborted) {
        void doc.destroy().catch(() => {});
        return;
      }
      currentDoc = doc;
      viewer.setDocument(doc);
      linkService.setDocument(doc);
    })().catch((error: unknown) => {
      if (!signal.aborted) latest.current.onError(error);
    });
    return () => {
      attempt.abort();
      setZoomRef.current = null;
      try {
        dispose?.();
      } catch {
        // The discarded viewer has no remaining surface for a teardown error.
      }
      pages.replaceChildren();
    };
  }, [blob, scrollerRef, pagesRef, scaleFactor]);
}

const mountedScrollers = new Set<HTMLElement>();

function usePdfZoomControls(
  onZoom: ((direction: PdfZoomDirection) => void) | null,
  scrollerRef: RefObject<HTMLElement | null>,
): void {
  const onZoomRef = useRef(onZoom);
  useEffect(() => {
    onZoomRef.current = onZoom;
  });
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    mountedScrollers.add(scroller);
    let hovered = false;
    const onEnter = () => {
      hovered = true;
    };
    const onLeave = () => {
      hovered = false;
    };
    scroller.addEventListener('mouseenter', onEnter);
    scroller.addEventListener('mouseleave', onLeave);
    const inScope = (target: EventTarget | null): boolean => {
      if (mountedScrollers.size <= 1) return true;
      if (hovered) return true;
      return target instanceof Node && scroller.contains(target);
    };
    const KEYS: Record<string, PdfZoomDirection> = { '+': 1, '=': 1, '-': -1, '0': 0 };
    const onKey = (e: KeyboardEvent) => {
      const zoom = onZoomRef.current;
      const direction = KEYS[e.key];
      if (!zoom || !(e.metaKey || e.ctrlKey) || direction === undefined) return;
      if (!inScope(e.target)) return;
      e.preventDefault();
      zoom(direction);
    };
    const onWheel = (e: WheelEvent) => {
      const zoom = onZoomRef.current;
      if (!zoom || !(e.metaKey || e.ctrlKey)) return;
      if (!(e.target as HTMLElement | null)?.closest?.('.pdf-scroller')) return;
      e.preventDefault();
      zoom(e.deltaY < 0 ? 1 : -1);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('wheel', onWheel);
      mountedScrollers.delete(scroller);
      scroller.removeEventListener('mouseenter', onEnter);
      scroller.removeEventListener('mouseleave', onLeave);
    };
  }, [scrollerRef]);
}

export interface PdfZoomLabels {
  zoomOut: string;
  zoomIn: string;
  reset: string;
  level: (zoom: number) => string;
}

interface PdfRegionBaseProps extends HTMLAttributes<HTMLElement> {
  blob?: Blob | readonly Blob[] | null;
  scaleFactor?: number;
  zoomLabels: PdfZoomLabels;
  placeholder?: ReactNode;
  notice?: ReactNode;

  imageAlt?: string;

  inline?: boolean;
  reading?: boolean;
}

type PdfRegionLoadingProps =
  | { loading?: false; loadingLabel?: undefined }
  | {
      loading: boolean;
      loadingLabel: ReactNode;
    };

export type PdfRegionProps = PdfRegionBaseProps & PdfRegionLoadingProps;

function describeError(error: unknown): string {
  let text = '';
  try {
    text = String(error).trim();
  } catch {
    // A non-stringifiable thrown value still needs a visible fallback.
  }
  return text || Object.prototype.toString.call(error);
}

function imageDecodeFailure(blob: Blob | null): Error {
  const type = blob?.type;
  return new Error(
    `The image could not be decoded${type ? ` (${type})` : ''}. ` +
      'A failed <img> reports no reason: the bytes may be truncated, may not be an ' +
      'image at all, or may be in a format this browser cannot read.',
  );
}

interface PdfFailure {
  blob: Blob | readonly Blob[] | null;
  scaleFactor: number;
  error: unknown;
}

function startwards(scroller: HTMLElement): number {
  return getComputedStyle(scroller).direction === 'rtl' ? -1 : 1;
}

function middleAcross(scroller: HTMLElement): number {
  const width = scroller.scrollWidth;
  return width > 0 ? (startwards(scroller) * scroller.scrollLeft + scroller.clientWidth / 2) / width : 0.5;
}

function keepMiddleAcross(scroller: HTMLElement, across: number) {
  scroller.scrollLeft = startwards(scroller) * (across * scroller.scrollWidth - scroller.clientWidth / 2);
}

function keepMiddleInView(scroller: HTMLElement, before: { top: number; across: number }, growth: number) {
  const height = scroller.clientHeight;
  const padding = parseFloat(getComputedStyle(scroller).paddingTop) || 0;
  scroller.scrollTop = padding + (before.top + height / 2 - padding) * growth - height / 2;
  keepMiddleAcross(scroller, before.across);
}

export function PdfRegion(
  { ref, blob = null,
    loading = false,
    loadingLabel,
    scaleFactor = 1,
    placeholder,
    notice,
    imageAlt = '',
    zoomLabels,
    inline = false,
    reading = false,
    className,
    children,
    ...rest
  }: PdfRegionProps & { ref?: Ref<HTMLElement> },
) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const [failure, setFailure] = useState<PdfFailure | null>(null);
  if (
    failure &&
    (failure.blob !== blob ||
      failure.scaleFactor !== scaleFactor)
  ) {
    setFailure(null);
  }

  const single = blob && 'type' in blob ? blob : null;
  const blobs: readonly Blob[] = single ? [single] : blob && !('type' in blob) ? blob : [];
  const isImage = blobs.length > 0 && blobs.every((b) => b.type.startsWith('image/'));
  const [imgUrls, setImgUrls] = useState<string[]>([]);
  useEffect(() => {
    if (!isImage) {
      setImgUrls([]);
      return;
    }
    const urls = blobs.map((b) => URL.createObjectURL(b));
    setImgUrls(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob, isImage]);
  const reportFailure = (error: unknown) => {
    setFailure({ blob, scaleFactor, error });
  };
  const [chosenZoom, setChosenZoom] = useState({ blob, zoom: 1 });
  const zoom = chosenZoom.blob === blob ? chosenZoom.zoom : 1;
  const zoomAnchor = useRef<{ blob: typeof blob; zoom: number; top: number; across: number } | null>(null);
  const changeZoom = (direction: PdfZoomDirection) => {
    const scroller = scrollerRef.current;
    if (scroller && !zoomAnchor.current && stepPdfZoom(zoom, direction) !== zoom) {
      zoomAnchor.current = { blob, zoom, top: scroller.scrollTop, across: middleAcross(scroller) };
    }

    setChosenZoom((chosen) => ({
      blob,
      zoom: stepPdfZoom(chosen.blob === blob ? chosen.zoom : 1, direction),
    }));
  };
  useLayoutEffect(() => {
    const anchor = zoomAnchor.current;
    const scroller = scrollerRef.current;
    zoomAnchor.current = null;
    if (!isImage || !anchor || !scroller || anchor.blob !== blob || anchor.zoom === zoom) return;
    keepMiddleInView(scroller, anchor, zoom / anchor.zoom);
  }, [zoom, blob, isImage]);
  const showsDocument = blobs.length > 0 && !loading && !failure;
  usePdfViewer(scrollerRef, pagesRef, isImage ? null : single, scaleFactor, reportFailure, zoom);
  usePdfZoomControls(showsDocument ? changeZoom : null, scrollerRef);
  const scrollBars = (
    <>
      <ScrollBar scrollerRef={scrollerRef} />
      <ScrollBar scrollerRef={scrollerRef} axis="x" />
    </>
  );
  return (
    <>
      <main ref={ref} className={cx('pdf-region', inline && 'is-inline', reading && 'is-reading', className)} {...rest}>
        <div
          className="pdf-scroller"
          data-scroll-host
          ref={scrollerRef}
          style={{ '--pdf-zoom': zoom } as CSSProperties}
        >

          {notice}
          <div className="pdfViewer pdf-pages" ref={pagesRef} />
          {imgUrls.map((imgUrl, i) => (
            <img
              key={imgUrl}
              className="pdf-image"
              src={imgUrl}
              alt={imageAlt}

              onLoad={(e) => {
                const { naturalWidth } = e.currentTarget;
                if (naturalWidth > 0) e.currentTarget.style.setProperty('--pdf-image-width', `${naturalWidth}px`);
              }}

              onError={() => reportFailure(imageDecodeFailure(blobs[i] ?? null))}
            />
          ))}
        </div>

        {loading && !failure && (
          <div className="card is-loading is-overlay" aria-busy="true">
            <p className="card-loading-label">{loadingLabel}</p>
          </div>
        )}
        {blobs.length === 0 && !loading && <div className="pdf-placeholder">{placeholder}</div>}

        {failure && (
          <div className="pdf-error" role="alert">

            {describeError(failure.error)}
          </div>
        )}
        {showsDocument && (
          <PillCluster className="pill--tip-above pdf-zoom">
            <Pill>
            <Button
              iconOnly
              title={zoomLabels.zoomOut}
              disabled={zoom <= PDF_ZOOM.min}
              onClick={() => changeZoom(-1)}
            >
              <Icon name="i-minus" />
            </Button>
            </Pill>
            <Pill>
            <Button className="pill-value" title={zoomLabels.reset} onClick={() => changeZoom(0)}>
              {zoomLabels.level(zoom)}
            </Button>
            </Pill>
            <Pill>
            <Button
              iconOnly
              title={zoomLabels.zoomIn}
              disabled={zoom >= PDF_ZOOM.max}
              onClick={() => changeZoom(1)}
            >
              <Icon name="i-plus" />
            </Button>
            </Pill>
          </PillCluster>
        )}
        {children}
        {inline && scrollBars}
      </main>
      {!inline && scrollBars}
    </>
  );
}
